import { useState } from 'react';
import { toast } from 'react-toastify';
import { ConversationPrompts } from 'utils/gloo-types';
import { useThunkDispatch } from 'utils/reduxHook';
import { closeModal } from 'actions/modal';
import { updateConversationPrompt } from 'actions/requests';
import Typography from 'components/atoms/Typography';
import ModalWrapper, { ModalFooter } from './ModalWrapper';

type ChangeConversationPromptStatusProps = {
  prompt: ConversationPrompts;
  title?: string;
  type: string;
  updatePromptInList: (prompt: ConversationPrompts) => void;
};

function ChangeConversationPromptStatus({
  title = 'Are You Sure?',
  prompt,
  type,
  updatePromptInList,
}: ChangeConversationPromptStatusProps) {
  const dispatch = useThunkDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const updatedPrompt = await dispatch(
        updateConversationPrompt(prompt?.id, {
          ...prompt,
          status: type === 'archive' ? 'Archived' : prompt?.status === 'Published' ? 'Unpublished' : 'Published',
        }),
      );
      updatePromptInList(updatedPrompt);
      toast.success(`Conversation Prompt ${updatedPrompt?.status} Successfully`);
      dispatch(closeModal());
    } catch (error) {
      toast.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalWrapper disableDismiss>
      <Typography className="mb-5 text-left text-glooCharcoal" variant="h1">
        {title}
      </Typography>
      <div className="space-y-2">
        <Typography variant="base">
          {`Are you sure you want to ${
            type === 'archive' ? 'Archive' : prompt?.status === 'Published' ? 'Unpublish' : 'Publish'
          }`}
          <span className="font-extrabold"> {prompt?.name}?</span>
        </Typography>
        {type === 'archive' ? (
          <Typography variant="base">This action cannot be undone.</Typography>
        ) : (
          <Typography variant="base">
            {prompt?.status === 'Unpublished'
              ? 'It will be available to everybody in your organization.'
              : 'It will be not be available to access in Inbox'}
          </Typography>
        )}
      </div>
      <ModalFooter
        className="mt-8"
        justifyButtons="end"
        isLoading={isLoading}
        primary={{
          type: type === 'archive' ? 'destruction' : 'primary',
          text: type === 'archive' ? 'Archive' : prompt?.status === 'Published' ? 'Unpublish' : 'Publish',
        }}
        secondary={{
          type: 'tertiary',
          text: 'Cancel',
        }}
        onConfirm={handleConfirm}
      />
    </ModalWrapper>
  );
}

export default ChangeConversationPromptStatus;
